import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Button } from '@vacasa/react-components-lib';
import './Survey.scss';
import { Form, Formik, FormikProps } from 'formik';
import SwipeableViews from 'react-swipeable-views';
import {
    ActionSegment,
    DetailedError,
    ErrorTypes,
    GeneralInformation,
    Question,
    QuestionType,
    SurveyResponse,
    SurveyResponseAnswers,
    SurveySources,
    SurveyType,
} from '@reviews/interfaces';
import { UnitPanel } from '../UnitPanel/UnitPanel';
import { MessagePanel } from '../MessagePanel/MessagePanel';
import { usePostSurveyResponseMutation } from '../../services';
import { Spinner } from '../../assests/icons';
import segmentService from '../../services/segment/segmentService';
import { OptimizelyContext } from '@optimizely/react-sdk';
import { Events } from '../../types/split.type';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AppRoutes } from '../../Router';
import _ from 'lodash';
import { PriorPhoto } from '../PhotoUpload/PhotoUpload';
import { buildSurveyResponseAnswers } from '../../utils/survey-response-answers.util';

export type SurveyInfo = {
    unit: {
        name: string;
        city: string;
        state: string;
        img: string;
    };
    general_information: GeneralInformation;
    survey_name?: string;
    survey_type?: SurveyType;
    prior_content?: PriorPhoto[];
    prior_tip?: string | null;
};

export type SurveyWizardStep = {
    component: JSX.Element;
    title: string;
    description: string;
    validationSchema: any;
    rawQuestions: Array<Question>;
};

interface SurveyWizardProps {
    steps: Array<SurveyWizardStep>;
    surveyInfo: SurveyInfo;
    showWizardStepNavigation?: boolean;
    isPal?: boolean;
    surveyType?: SurveyType;
    surveySource?: SurveySources;
}

const getLivePhotoCount = (values: { [key: string]: any } | undefined, questions: Question[]): number => {
    const photoQuestion = questions.find((question) => question.type === QuestionType.PHOTO);
    if (!photoQuestion) {
        return 0;
    }
    const key = `${photoQuestion.question_id}-${photoQuestion.name}-${photoQuestion.type}-answer`;
    const value = values?.[key];
    return Array.isArray(value) ? value.length : 0;
};

export function SurveyWizard(props: SurveyWizardProps) {
    const { steps, surveyInfo, isPal, surveyType = SurveyType.REVIEW, surveySource } = props;
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { optimizely } = useContext(OptimizelyContext);
    const [currentStep, setCurrentStep] = useState(0);
    const [postSurveyResponse, { isLoading: isSubmittingResponse, data: submitResponse, error: submitError }] =
        usePostSurveyResponseMutation();
    const isContentSurvey = surveyType === SurveyType.CONTENT;

    const formRef = useRef<FormikProps<any>>(null);

    useLayoutEffect(() => {
        window.scrollTo(0, 0);
    });

    useEffect(() => {
        (async function validateForm() {
            const currentValues = formRef.current?.values;
            await formRef.current?.validateForm(currentValues);
        })();
    }, [currentStep]);

    const isLastStep = () => {
        return currentStep === steps.length - 1;
    };

    const handleSubmit = async (values: { [key: string]: any }, questions: Question[]) => {
        if (!isLastStep()) {
            handleNext();
            segmentService.trackEvent({
                action: ActionSegment.NEXT,
            });
            return;
        }
        const answers = buildSurveyResponseAnswers(values, questions);
        const surveyResponse = transformToSurveyResponse(answers, surveyInfo.general_information);

        try {
            const resultSurvey = await postSurveyResponse({
                surveyID: surveyInfo.general_information.survey_id,
                response: surveyResponse,
            }).unwrap();
            segmentService.trackEvent({
                action: ActionSegment.SUBMIT,
                review_id: resultSurvey.data.attributes.review_id,
            });
            optimizely?.track(Events.REVIEWS_APP_SUBMIT_EVENT);
        } catch (_e) {
            navigate(AppRoutes.HOME, { state: { kind: ErrorTypes.UNHANDLED } });
        }
    };

    const transformToSurveyResponse = (valuesReview: any, generalInformation: GeneralInformation): SurveyResponse => {
        const source = surveySource || (isPal ? SurveySources.PAL_GUEST : SurveySources.GUEST);

        return {
            first_name: generalInformation.first_name,
            last_name: generalInformation.last_name,
            email: generalInformation.email,
            type: surveyType,
            admin_reservation_id: Number(generalInformation.admin_reservation_id),
            admin_unit_id: Number(generalInformation.admin_unit_id),
            source,
            review_request_origin: searchParams.get('review_request_origin') ?? undefined,
            survey_response_answers: valuesReview as Array<SurveyResponseAnswers>,
            survey_test_information: undefined,
        };
    };

    const handleNext = () => {
        const nextStep = Math.min(currentStep + 1, steps.length - 1);
        setCurrentStep(nextStep);
    };

    const activeStep = steps[currentStep];
    const validationSchema = activeStep?.validationSchema || {};

    if (isSubmittingResponse) {
        return (
            <div className="loading">
                <Spinner />
            </div>
        );
    }

    if (submitError) {
        const errorKind = (submitError as DetailedError).code || ErrorTypes.UNHANDLED;
        return <MessagePanel kind={errorKind as ErrorTypes} action={ActionSegment.SUBMIT} />;
    }

    if (submitResponse) {
        return <MessagePanel kind="THANKS" action={null} />;
    }

    const {
        general_information: { first_name, last_name },
        unit,
    } = surveyInfo;

    const allQuestions = _.reduce(
        steps,
        (acc, curr) => {
            acc.push(...curr.rawQuestions);
            return acc;
        },
        [] as Question[]
    );

    const hasPhotoQuestion = allQuestions.some((question) => question.type === QuestionType.PHOTO);
    const isReviewWithContent = !isContentSurvey && hasPhotoQuestion;

    return (
        <React.Fragment>
            <div
                id={`feedback-B`}
                className={`mt-3 ${isContentSurvey ? 'content-survey' : ''} ${isReviewWithContent ? 'review-with-content' : ''}`}
            >
                <Formik
                    innerRef={formRef}
                    initialValues={{}}
                    onSubmit={(values) => handleSubmit(values, allQuestions)}
                    validateOnMount={true}
                    validationSchema={validationSchema}
                >
                    {({ isValid, submitForm, values }) => {
                        const livePhotoCount = getLivePhotoCount(values, allQuestions);
                        const needsPhotosToContinue = isContentSurvey && (!isValid || livePhotoCount === 0);
                        const submitLabel = (() => {
                            if (!isLastStep()) {
                                return 'Next';
                            }
                            if (!isContentSurvey) {
                                return 'Submit';
                            }
                            if (needsPhotosToContinue) {
                                return 'Add photos to continue';
                            }
                            return `Submit ${livePhotoCount} Photo${livePhotoCount === 1 ? '' : 's'}`;
                        })();

                        const submitButtonClass = (() => {
                            if (isContentSurvey) {
                                return `content-survey__submit${needsPhotosToContinue ? ' content-survey__submit--needs-photos' : ''}`;
                            }
                            if (isReviewWithContent) {
                                return 'review-with-content__submit';
                            }
                            return '';
                        })();

                        return (
                            <React.Fragment>
                                <Form
                                    onSubmit={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                    }}
                                >
                                    <SwipeableViews index={currentStep} disabled={true}>
                                        {steps &&
                                            steps.map((_step: any, index: number) => {
                                                if (currentStep !== index) {
                                                    return <React.Fragment key={index} />;
                                                }

                                                return (
                                                    <div className="survey-section-step" key={index}>
                                                        {isContentSurvey ? (
                                                            <div className="content-survey__header">
                                                                <div className="content-survey__eyebrow">WELCOME BACK</div>
                                                                <h1 className="content-survey__greeting">Hi, {first_name || 'there'}!</h1>
                                                                <p className="content-survey__intro">
                                                                    Show us your favorite spots at <strong>{unit.name}</strong>. Your photos
                                                                    help future guests feel at home.
                                                                </p>
                                                            </div>
                                                        ) : (
                                                            <>
                                                                <h1 className={'h1 base-font-title fix-title'}>{steps[index].title}</h1>
                                                                <p className={'base-font-body sub-title-rating'}>
                                                                    {steps[index].description}
                                                                </p>
                                                            </>
                                                        )}
                                                        {!isContentSurvey && index === 0 && <UnitPanel {...unit} />}
                                                        {steps[index].component}
                                                        {!isContentSurvey && index === 0 && (
                                                            <div className={'field-wrapper caption name-change'}>
                                                                <div className={'base-font-body category-description'}>
                                                                    {`You're giving feedback as  ${first_name} ${last_name
                                                                        .charAt(0)
                                                                        .toUpperCase()}`}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                    </SwipeableViews>
                                    {steps.length > 0 && (
                                        <div
                                            className={`row field-wrapper ${
                                                isContentSurvey || isReviewWithContent ? 'content-survey__actions' : 'flex-row-reverse'
                                            }`}
                                        >
                                            <div
                                                className={
                                                    isContentSurvey || isReviewWithContent
                                                        ? 'col-12'
                                                        : 'col-md-6 col-md-offset-6 d-flex flex-row-reverse'
                                                }
                                            >
                                                <Button
                                                    customClass={`button body-regular ${submitButtonClass}`}
                                                    variant={'primary'}
                                                    disabled={!isValid}
                                                    onClick={submitForm}
                                                >
                                                    {needsPhotosToContinue && (
                                                        <span className="content-survey__submit-icon" aria-hidden="true">
                                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                                                <path d="M9 16h6v-6h4l-7-7-7 7h4v6zm-4 2h14v2H5v-2z" fill="currentColor" />
                                                            </svg>
                                                        </span>
                                                    )}
                                                    {submitLabel}
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                </Form>
                            </React.Fragment>
                        );
                    }}
                </Formik>
            </div>
        </React.Fragment>
    );
}
